import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete,Button,Card,Checkbox,Fab,FormControlLabel,IconButton,MenuItem,Paper,Stack,TextField,Typography } from "@mui/material";
import { useMutation,useQuery } from "@tanstack/react-query";
import {
  addEdge,
  applyEdgeChanges,
  Background,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  MiniMap,
  Node,
  OnEdgesChange,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ChangeEvent,useCallback,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from 'axios';
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from '../../helpers/matomo';
import Checklists from "../../service/Checklists";
import Fluxo from "../../service/Fluxo";
import Anonimo from '../../service/Anonimo';
import CadastrosAgendamentos from '../../service/CadastrosAgendamentos';
import queryClient from "../../service/query";
import { CadastroModelo, ChecklistModel,FluxoEntrada,FluxoModel,newEdgeType,NewFluxoModel,newNodeType } from "../../types/fluxo/types";
import CustomEdge from "./CustomEdge";
import CustomNode from "./CustomNode";

const formService = new Checklists();
const fluxoService = new Fluxo();
const anonimoService = new Anonimo();
const cadastrosService = new CadastrosAgendamentos();

const nodeTypes = {
  'customNode': CustomNode
};

const edgeTypes = {
  deletable: CustomEdge,
};

function validarRespostaFluxo<T extends { data?: { success?: boolean; error?: string; message?: string } }>(response: T) {
  const dados = response.data;
  if (dados?.success === false) throw new Error(dados.error || dados.message || 'Não foi possível salvar o fluxo.');
  return response;
}

function mensagemErroFluxo(error: unknown, mensagemPadrao: string) {
  if (axios.isAxiosError(error)) {
    const dados = error.response?.data as { error?: string; message?: string } | undefined;
    return dados?.error || dados?.message || mensagemPadrao;
  }
  return error instanceof Error && error.message ? error.message : mensagemPadrao;
}

interface CreateAndEditProps {
  fluxoParaEditar?: FluxoModel;
  setFluxoParaEditar?: React.Dispatch<React.SetStateAction<FluxoModel | undefined>>;
}

export default function CreateAndEdit({ fluxoParaEditar, setFluxoParaEditar }: CreateAndEditProps) {
  const { t } = useTranslation('translation');
  const [nomeFluxo, setNomeFluxo] = useState('Novo Fluxo');
  const [exigeCheckinAnonimo, setExigeCheckinAnonimo] = useState(false);
  const [checklistCheckinId, setChecklistCheckinId] = useState('');
  const [idModeloCadastro, setIdModeloCadastro] = useState('');
  const [perguntaValidacao, setPerguntaValidacao] = useState('');
  const [tentativasMaximas, setTentativasMaximas] = useState(1);
  const [confirmacaoSemAgendamento, setConfirmacaoSemAgendamento] = useState('redigitar_identificador');
  const [fluxoSelecionado, setFluxoSelecionado] = useState<NewFluxoModel>();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);

  //#region: - Api que busca modelos de formulários
  const { data: formsData, isLoading: loadingForms, isFetching: fetchingForms } = useQuery({
    queryKey: ['GET_FORMS'],
    queryFn: async () => {
      trackEventMatomo('Tela de criação e edição de fluxo', 'api busca modelos de forms', '', '');
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.erroBuscaForms'));
      const response: { id: string, nome: string }[] | undefined = await formService.getSimpleForm(token, false);
      if (!response) throw new Error(t('fluxo.erroBuscaForms'));
      return response;
    },
    retry: 3,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });//#endregion

  const { data: formulariosAnonimos, isLoading: loadingFormulariosAnonimos } = useQuery({
    queryKey: ['GET_MODELOS_ANONIMOS_FLUXO'],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.anonymousCheckinLoadError'));
      const response = await anonimoService.listaModelosChecklistAnonimo(token);
      if (!Array.isArray(response)) throw new Error(t('fluxo.anonymousCheckinLoadError'));
      return response as { id: string; nome: string }[];
    },
    enabled: exigeCheckinAnonimo,
    refetchOnWindowFocus: false,
  });

  const { data: modelosCadastro, isLoading: loadingModelosCadastro } = useQuery({
    queryKey: ['CADASTRO_MODELOS_FLUXO'],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.erroBuscaForms'));
      return cadastrosService.listarModelosCadastro(token);
    },
    enabled: exigeCheckinAnonimo,
    refetchOnWindowFocus: false,
  });

  //#region: - Api que busca modelos de fluxo
  const { data: fluxoData, isLoading: loadingFluxo, isFetching: fetchingFluxo } = useQuery({
    queryKey: ['GET_FLUXOS'],
    queryFn: async () => {
      trackEventMatomo('Tela de criação e edição de fluxo', 'api busca modelos de fluxo', '', '');
      const response = toast.promise(async () => {
        const token = await AttToken();
        if (!token) throw new Error(t('fluxo.erroBusca'));
        const response = await fluxoService.getAll(token);
        if (!response?.data.success || !Array.isArray(response.data.data)) {
          throw new Error(t('fluxo.erroBusca'));
        }
        return response.data.data;
      }, {
        pending: t('fluxo.buscaFluxos'),
        success: t('fluxo.modelsFind'),
        error: t('fluxo.erroBusca')
      }, { toastId: 'buscaFLuxos' });
      return response;
    },
    retry: 3,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });//#endregion

  function verificaAdicionados(fluxo: FluxoModel[]): string[] {
    const idsFluxo = fluxo?.flatMap((fluxo: FluxoModel) => fluxo.fluxo?.map((ele: ChecklistModel) => ele.checklistId) ?? []) ?? [];
    return idsFluxo;
  }

  //#region: - Api que cria modelos
  const { mutate: create, isPending: loadingCreate } = useMutation({
    mutationKey: ['CREATE_FLOW'],
    mutationFn: async () => {
      trackEventMatomo('Tela de criação e edição de fluxo', 'api criação de modelos de fluxo', '', '');
      const response = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const fluxo: { checklistId: string; proximoChecklist?: string }[] = edges.map(edge => ({
            checklistId: edge.source,
            proximoChecklist: edge.target
          }));
          const ultimoChecklistId = edges[edges.length - 1]?.target;
          if (ultimoChecklistId) {
            fluxo.push({ checklistId: ultimoChecklistId });
          }

          const fluxoModelo = {
            nome: nomeFluxo,
            entrada: {
              exigeCheckinAnonimo,
              modoLiberacao: exigeCheckinAnonimo ? 'apos_checkin_validado' as const : 'apos_agendamento' as const,
              publicoAtivo: exigeCheckinAnonimo,
              ...(exigeCheckinAnonimo ? {
                checklistCheckinId,
                validacaoAgendamento: { idModeloCadastro, pergunta: perguntaValidacao, tentativasMaximas, confirmacaoSemAgendamento },
              } : {}),
            },
            fluxo
          };
          return validarRespostaFluxo(await fluxoService.create({ ...token }, fluxoModelo));
        }
      }, {
        pending: t('fluxo.adding'),
        success: t('fluxo.created'),
        error: { render: ({ data: error }) => mensagemErroFluxo(error, t('fluxo.error')) }
      }, { toastId: 'createFluxo' });
      return response;
    },
    onSuccess: (data) => {
      if (data) {
        setFluxoSelecionado({
          id: data.data.data.id,
          nome: data.data.data.nome,
          fluxo: data.data.data.fluxo,
          empresa: data.data.data.empresa,
          dataCriacao: data.data.data.dataCriacao,
        });
        setNomeFluxo(data.data.data.nome);
      }
      queryClient.refetchQueries({ queryKey: ['GET_FLUXOS'] });
    },
  });//#endregion

  //#region: - Api que atualiza modelos
  const { mutate: updateModelo, isPending: loadingUpdate } = useMutation({
    mutationKey: ['UPDATE_FLOW'],
    mutationFn: async () => {
      trackEventMatomo('Tela de criação e edição de fluxo', 'api atualização de modelos de fluxo', '', '');
      const response = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const fluxoModelo: { nome: string; entrada: FluxoEntrada; fluxo: { checklistId: string; proximoChecklist?: string }[] } = {
            nome: nomeFluxo,
            entrada: {
              exigeCheckinAnonimo,
              modoLiberacao: exigeCheckinAnonimo ? 'apos_checkin_validado' as const : 'apos_agendamento' as const,
              publicoAtivo: exigeCheckinAnonimo,
              ...(exigeCheckinAnonimo ? {
                checklistCheckinId,
                validacaoAgendamento: { idModeloCadastro, pergunta: perguntaValidacao, tentativasMaximas, confirmacaoSemAgendamento },
              } : {}),
            },
            fluxo: edges.map(edge => ({
              checklistId: edge.source,
              proximoChecklist: edge.target
            }))
          };
          fluxoModelo.fluxo.push({ checklistId: edges[edges.length - 1]?.target });
          return validarRespostaFluxo(await fluxoService.update({ ...token }, fluxoSelecionado?.id as string, fluxoModelo));
        }
      }, {
        pending: t('fluxo.update'),
        success: t('fluxo.updateSuccess'),
        error: { render: ({ data: error }) => mensagemErroFluxo(error, t('fluxo.updateError')) }
      }, { toastId: 'updateFluxo' });
      return response;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_FLUXOS'] });
    },
  });//#endregion

  //#region: - Api que deleta modelos
  const { mutate: deleteFluxo, isPending: loadingDelete } = useMutation({
    mutationKey: ['DELETE_FLOW'],
    mutationFn: async () => {
      trackEventMatomo('Tela de criação e edição de fluxo', 'api deleção de modelos de fluxo', '', '');
      const response = toast.promise(async () => {
        const token = await AttToken();
        if (token) {
          const response = await fluxoService.delete({ ...token }, fluxoSelecionado?.id as string);
          return response;
        }
      }, {
        pending: t('fluxo.delete'),
        success: t('fluxo.successDelete'),
        error: t('fluxo.errorDelete')
      }, { toastId: 'deleteFluxo' });
      return response;
    },
    onSuccess: () => {
      setFluxoSelecionado(undefined);
      setNomeFluxo('');
      setExigeCheckinAnonimo(false);
      setChecklistCheckinId('');
      setNodes([]);
      setEdges([]);
      queryClient.refetchQueries({ queryKey: ['GET_FLUXOS'] });
    },
  });//#endregion

  //#region: - Função para editar nome do fluxo
  function handleNome(event: ChangeEvent<HTMLInputElement>) {
    trackEventMatomo('Tela de criação e edição de fluxo', 'edita nome do fluxo', '', '');
    setNomeFluxo(event.target.value);
  }//#endregion

  //#region: - Função que remove nós
  const handleRemoveEdge = useCallback((id: string) => {
    trackEventMatomo('Tela de criação e edição de fluxo', 'une modelos de fluxo', '', '');
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
  }, [setEdges]);//#endregion

  //#region: - Função que controla junção de nós
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot));
    },
    [setEdges],
  );//#endregion

  //#region: - Função que conecta nodes
  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((edgesSnapshot) =>
        addEdge({
          ...params,
          type: 'deletable',
          data: { onDelete: handleRemoveEdge }
        }, edgesSnapshot)
      ),
    [setEdges, handleRemoveEdge],
  );

  //#region: - Função que remove nodes
  const handelRemoveNode = useCallback((id: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== id));
    setEdges((eds) => eds.filter((edge) => edge.source !== id && edge.target !== id));
  }, [setEdges, setNodes]);//#endregion

  //#region: - Função que altera labels de nodes
  const handleChangeLabel = useCallback((id: string, newId: string, label: string) => {
    setNodes((nds: Node[]) => nds.map((node) => {
      if (node.id === id) {
        return {
          ...node,
          id: newId,
          data: {
            id: newId,
            label: label,
            select: formsData,
            onDelete: handelRemoveNode,
            onEdgesChange: onEdgesChange,
            onEdgesREmove: handleRemoveEdge,
            onChangeLabel: handleChangeLabel,
            onAddNode: addNewNode,
          }
        };
      }
      return node;
    }));
    setEdges((eds) => eds.map((edge) => {
      if (edge.source === id) {
        return { ...edge, source: newId, id: `e-${newId}-${edge.target}` };
      }
      if (edge.target === id) {
        return { ...edge, target: newId, id: `e-${edge.source}-${newId}` };
      }
      return edge;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formsData, handelRemoveNode, setNodes, handelRemoveNode, handleRemoveEdge, onEdgesChange]);//#endregion

  //#region: - Função que adciona novos nodes
  const addNewNode = useCallback((sourceId: string) => {
    const newId = crypto.randomUUID();
    setNodes((nds) => {
      const sourceNode = nds.find((n) => n.id === sourceId);
      const baseNode = sourceNode || nds[nds.length - 1];
      const randomX = Math.floor(Math.random() * (550 - 350 + 1)) + 350;
      const randomY = Math.floor(Math.random() * (100 - (-100) + 1)) + (-100);

      const newPosition = baseNode
        ? {
          x: baseNode.position.x + randomX,
          y: baseNode.position.y + randomY
        }
        : { x: 100, y: 100 };

      const newNode: Node = {
        id: newId,
        position: newPosition,
        type: 'customNode',
        data: {
          id: newId,
          label: '',
          select: formsData,
          onDelete: handelRemoveNode,
          onChangeLabel: handleChangeLabel,
          onAddNode: addNewNode,
        }
      };
      if (sourceId) {
        setEdges((eds) => {
          const existingEdge = eds.find((e) => e.source === sourceId);

          if (existingEdge) {
            const filteredEdges = eds.filter((e) => e.id !== existingEdge.id);
            return [
              ...filteredEdges,
              { id: `e-${sourceId}-${newId}`, source: sourceId, target: newId, type: 'deletable', data: { onDelete: handleRemoveEdge } },
              { id: `e-${newId}-${existingEdge.target}`, source: newId, target: existingEdge.target, type: 'deletable', data: { onDelete: handleRemoveEdge } }
            ];
          }

          return [...eds, { id: `e-${sourceId}-${newId}`, source: sourceId, target: newId, type: 'deletable', data: { onDelete: handleRemoveEdge } }];
        });
      }

      return [...nds, newNode];
    });
  }, [formsData, handelRemoveNode, handleChangeLabel, handleRemoveEdge, setNodes, setEdges]);//#endregion

  //#region: - Efeito para carregar fluxo para edição a partir da aba de acompanhamento
  const carregarFluxo = useCallback((value: FluxoModel) => {
    setNomeFluxo(value.nome);
    setExigeCheckinAnonimo(Boolean(value.entrada?.exigeCheckinAnonimo));
    setChecklistCheckinId(value.entrada?.checklistCheckinId ?? '');
    setIdModeloCadastro(value.entrada?.validacaoAgendamento?.idModeloCadastro ?? '');
    setPerguntaValidacao(value.entrada?.validacaoAgendamento?.pergunta ?? '');
    setTentativasMaximas(value.entrada?.validacaoAgendamento?.tentativasMaximas ?? 1);
    setConfirmacaoSemAgendamento(value.entrada?.validacaoAgendamento?.confirmacaoSemAgendamento ?? 'redigitar_identificador');
    setFluxoSelecionado(value);

    const newNodes: newNodeType[] | undefined = [];
    const newEdges: newEdgeType[] = [];

    value?.fluxo?.forEach((item: ChecklistModel, index: number) => {
      const baseNode = newNodes[index - 1];
      const randomX = Math.floor(Math.random() * 201) + 350;
      const randomY = Math.floor(Math.random() * 201) - 100;

      newNodes.push({
        id: item.checklistId,
        position: baseNode ? { x: baseNode.position.x + randomX, y: baseNode.position.y + randomY } : { x: 100, y: 100 },
        type: 'customNode',
        data: {
          id: item.checklistId,
          label: '',
          select: formsData,
          onEdgesChange,
          onEdgesREmove: handleRemoveEdge,
          onDelete: handelRemoveNode,
          onChangeLabel: handleChangeLabel,
          onAddNode: addNewNode,
        },
      });
  
      if (item.proximoChecklist) {
        newEdges.push({
          id: `e-${item.checklistId}-${item.proximoChecklist}`,
          source: item.checklistId,
          target: item.proximoChecklist,
          type: 'deletable',
          data: { onDelete: handleRemoveEdge },
        });
      }
    });
    setNodes(newNodes);
    setEdges(newEdges);
  }, [addNewNode, formsData, handelRemoveNode, handleChangeLabel, handleRemoveEdge, onEdgesChange, setEdges, setNodes]);

  useEffect(() => {
    if (fluxoParaEditar && !loadingForms && !fetchingForms && formsData) {
      carregarFluxo(fluxoParaEditar as FluxoModel);

      if (setFluxoParaEditar) {
        setFluxoParaEditar(undefined);
      }
    }
  }, [fluxoParaEditar, loadingForms, fetchingForms, formsData, carregarFluxo, setFluxoParaEditar]);//#endregion

  const onLoading = (loadingFluxo || fetchingFluxo) ||
    (loadingForms || fetchingForms)
    || loadingCreate
    || loadingDelete
    || loadingUpdate;
  const dadosCheckinObrigatoriosPreenchidos = Boolean(
    checklistCheckinId
    && idModeloCadastro
    && perguntaValidacao.trim()
    && tentativasMaximas >= 1
    && confirmacaoSemAgendamento,
  );

  const createModelo = () => {
    setFluxoSelecionado({
      nome: nomeFluxo,
      fluxo: [],
      dataCriacao: new Date().getTime(),
    });
    const newId = crypto.randomUUID();
    setNodes([{
      id: newId,
      position: { x: 100, y: 100 },
      type: 'customNode',
      data: {
        id: newId,
        label: '',
        select: formsData,
        onDelete: handelRemoveNode,
        onChangeLabel: handleChangeLabel,
        onAddNode: addNewNode,
      }
    }]);
    setNomeFluxo(nomeFluxo);
  };

  return (
    //#region: - Inicio componente
    <Stack direction='column' alignItems='center' justifyContent='flex-start' spacing={2}>
      <Stack direction='row' alignItems='center' spacing={2} width='100%'>
        <Stack direction='row' justifyContent='flex-start' alignItems='center' spacing={1} width='100%'>
          <IconButton
            disabled={
              (loadingForms || fetchingForms)
              || (loadingFluxo || fetchingFluxo)
              || loadingCreate
              || loadingDelete
              || loadingUpdate
              || fluxoSelecionado !== undefined}
            onClick={() => createModelo()}
            sx={{ fontWeight: 800, fontSize: 15, backgroundColor: '#00C4A7', borderRadius: 1, p: 1.5, color: 'white' }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
          <Autocomplete
            getOptionLabel={option => option.nome}
            getOptionKey={option => option.id ?? option.nome}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={fluxoSelecionado || null}
            disabled={onLoading}
            disablePortal
            onChange={(_eve, value, reason) => {
              if (reason === 'clear' || !value) {
                setFluxoSelecionado(undefined);
                setNomeFluxo('');
                setExigeCheckinAnonimo(false);
                setChecklistCheckinId('');
                setNodes([]);
                setEdges([]);
                return;
              }
              setNomeFluxo(value.nome);
              setFluxoSelecionado(value);
              setExigeCheckinAnonimo(Boolean(value.entrada?.exigeCheckinAnonimo));
              setChecklistCheckinId(value.entrada?.checklistCheckinId ?? '');
              setIdModeloCadastro(value.entrada?.validacaoAgendamento?.idModeloCadastro ?? '');
              setPerguntaValidacao(value.entrada?.validacaoAgendamento?.pergunta ?? '');
              setTentativasMaximas(value.entrada?.validacaoAgendamento?.tentativasMaximas ?? 1);
              setConfirmacaoSemAgendamento(value.entrada?.validacaoAgendamento?.confirmacaoSemAgendamento ?? 'redigitar_identificador');
              const newNodes: newNodeType[] = [];
              const newEdges: newEdgeType[] = [];
              const newId = crypto.randomUUID();
              if (value?.fluxo) {
                value?.fluxo.forEach((item, index) => {
                  const baseNode = newNodes[index - 1];
                  const randomX = Math.floor(Math.random() * (550 - 350 + 1)) + 350;
                  const randomY = Math.floor(Math.random() * (100 - (-100) + 1)) + (-100);

                  const newPosition = baseNode
                    ? {
                      x: baseNode.position.x + randomX,
                      y: baseNode.position.y + randomY,
                    }
                    : { x: 100, y: 100 };

                  newNodes.push({
                    id: item.checklistId,
                    position: newPosition,
                    type: 'customNode',
                    data: {
                      id: item.checklistId,
                      label: '',
                      select: formsData,
                      onEdgesChange: onEdgesChange,
                      onEdgesREmove: handleRemoveEdge,
                      onDelete: handelRemoveNode,
                      onChangeLabel: handleChangeLabel,
                      onAddNode: addNewNode,
                    },
                  });

                  if (item.proximoChecklist) {
                    newEdges.push({
                      id: `e-${item.checklistId}-${item.proximoChecklist}`,
                      source: item.checklistId,
                      target: item.proximoChecklist,
                      type: 'deletable',
                      data: { onDelete: handleRemoveEdge },
                    });
                  }
                });
              } else {
                newNodes.push({
                  id: newId,
                  position: { x: 100, y: 100 },
                  type: 'customNode',
                  data: {
                    id: newId,
                    label: '',
                    select: formsData,
                    onDelete: handelRemoveNode,
                    onChangeLabel: handleChangeLabel,
                    onAddNode: addNewNode,
                  }
                });
              }
              setNodes(newNodes);
              setEdges(newEdges);
              verificaAdicionados(fluxoData);
            }}
            options={
              fluxoData
                ?.filter((fluxo: FluxoModel) => !fluxo.dataDesativacao)
                ?.map((fluxo: FluxoModel) => ({ ...fluxo }))
              ?? []}
            fullWidth
            noOptionsText={t('fluxo.none')}
            renderInput={(params) => <TextField {...params} label={t('fluxo.select')} />}
          />
        </Stack>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Fab onClick={() => deleteFluxo()} color="error" size="small" disabled={
            !fluxoSelecionado
            || loadingDelete
            || loadingCreate
            || loadingUpdate
            || !fluxoSelecionado.id
          }>
            <DeleteIcon />
          </Fab>
          <Button
            onClick={() => fluxoSelecionado && fluxoSelecionado.id ? updateModelo() : create()}
            disabled={
              !nomeFluxo?.length
              || !nodes.length
              || !edges.length
              || (exigeCheckinAnonimo && !dadosCheckinObrigatoriosPreenchidos)
              || nodes?.some((ele) => !ele?.id.startsWith('-'))
              || loadingCreate
              || loadingDelete
              || loadingUpdate}
            sx={{ textTransform: 'none', fontWeight: 800, fontSize: 15, backgroundColor: '#00C4A7' }}
            variant='contained'
            endIcon={<SaveIcon />}>
            {t('save')}
          </Button>
        </Stack>
      </Stack>
      <Stack direction='column' justifyContent='center' alignItems='center' width='100%' height='100%'>
        {!fluxoSelecionado ?
          <Typography color='textDisabled' variant="h5" textAlign='center' paddingTop='20%'>{t('fluxo.add')}</Typography>
          : <Card sx={{ width: '100%', height: '100%' }}>
            <Stack direction='row' justifyContent='flex-start' alignItems='center' width='100%' padding={2}>
              <TextField sx={{ width: '400px' }} value={nomeFluxo} onChange={handleNome} label={t("fluxo.nome")} />
            </Stack>
            <Stack spacing={2} paddingX={2} paddingBottom={2}>
              <FormControlLabel
                control={<Checkbox checked={exigeCheckinAnonimo} onChange={(event) => {
                  setExigeCheckinAnonimo(event.target.checked);
                  if (!event.target.checked) setChecklistCheckinId('');
                }} />}
                label={t('fluxo.anonymousCheckinRequired')}
              />
              {exigeCheckinAnonimo && <TextField
                select
                required
                fullWidth
                label={t('fluxo.anonymousCheckinForm')}
                value={checklistCheckinId}
                onChange={(event) => setChecklistCheckinId(event.target.value)}
                disabled={loadingFormulariosAnonimos}
                helperText={formulariosAnonimos?.length ? undefined : t('fluxo.anonymousCheckinNoForms')}
              >
                {(formulariosAnonimos ?? []).map((formulario) => <MenuItem key={formulario.id} value={formulario.id}>{formulario.nome}</MenuItem>)}
              </TextField>}
              {exigeCheckinAnonimo && <>
                <TextField
                  select
                  required
                  fullWidth
                  label={t('checklists.publicAccess.registrationModel')}
                  value={idModeloCadastro}
                  onChange={(event) => setIdModeloCadastro(event.target.value)}
                  disabled={loadingModelosCadastro}
                  helperText={modelosCadastro?.length ? undefined : t('checklists.publicAccess.noActiveRegistrationModel')}
                >
                  {(modelosCadastro ?? []).map((modelo: CadastroModelo) => <MenuItem key={modelo.id} value={modelo.id}>{modelo.nome}</MenuItem>)}
                </TextField>
                <TextField required fullWidth label={t('checklists.publicAccess.question')} value={perguntaValidacao} onChange={(event) => setPerguntaValidacao(event.target.value)} />
                <TextField required fullWidth type='number' inputProps={{ min: 1 }} label={t('checklists.publicAccess.attempts')} value={tentativasMaximas} onChange={(event) => setTentativasMaximas(Math.max(1, Number(event.target.value) || 1))} />
                <TextField select required fullWidth label={t('checklists.publicAccess.confirmationMode')} value={confirmacaoSemAgendamento} onChange={(event) => setConfirmacaoSemAgendamento(event.target.value)}>
                  <MenuItem value='redigitar_identificador'>{t('checklists.publicAccess.retypeIdentifier')}</MenuItem>
                  <MenuItem value='digitar_ciente'>{t('checklists.publicAccess.typeAware')}</MenuItem>
                </TextField>
              </>}
            </Stack>
            <Paper sx={{ height: '50svh', width: '100%', padding: 2 }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                edgeTypes={edgeTypes}
                panOnScroll
                selectionOnDrag
                proOptions={{ hideAttribution: true }}
                fitView
              >
                <Background color='skyblue' />
                <Controls />
                <MiniMap nodeStrokeWidth={3} zoomable pannable />
              </ReactFlow>
            </Paper>
          </Card>}
      </Stack>
    </Stack >
    //#endregion
  );
}
